import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useSortBy,
  useExpanded,
  usePagination,
  useGlobalFilter
} from "react-table";
import classnames from "classnames";

const propTypes = {
  id: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  getRowStyle: PropTypes.func
};

const defaultProps = {
  getRowStyle: () => {}
};

const Table = ({
  id,
  columns,
  data,
  SubComponent,
  subComponentProps,
  onRowClick,
  getRowStyle,
  itemsPerPage,
  emptyStateText,
  defaultSortOrder = { idx: -1 },
  HeaderComponent,
  customSort,
  TableHeaderComponent,
  TableFooterComponent
}) => {
  const isClickable = !!onRowClick;
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setGlobalFilter,
    state: { pageIndex, globalFilter }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: itemsPerPage || 10 }
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination
  );

  const [sortState, setSortState] = useState(defaultSortOrder);
  const [searchQuery, setSearchQuery] = useState("");

  const doSearch = () => {
    setGlobalFilter(searchQuery);
  };

  const toRender = itemsPerPage ? page : rows;

  return (
    <>
      {HeaderComponent ? <HeaderComponent /> : null}
      {!HeaderComponent && itemsPerPage ? (
        <div className="flex items-center">
          <button
            className="btn p-1 w-10 mx-1"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
          >
            {"<<"}
          </button>
          <button
            className="btn p-1 w-10 mx-1"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            {"<"}
          </button>
          <button
            className="btn p-1 w-10 mx-1"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            {">"}
          </button>
          <button
            className="btn p-1 w-10 mx-1"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            {">>"}
          </button>
          <p className="ml-3 mr-6">
            Page{" "}
            <input
              className="p-1 w-10"
              value={pageIndex + 1}
              onChange={e => gotoPage(e.target.value - 1)}
              onClick={e => e.target.select()}
            />{" "}
            of {pageCount}
          </p>
          <p>
            Search:{" "}
            <input
              className="p-1"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => (e.key == "Enter" ? doSearch() : 0)}
            />
          </p>
          <button className="btn p-1 w-10 mx-1" onClick={() => doSearch()}>
            go
          </button>
          {!!globalFilter && (
            <button
              className="btn p-1 mx-1"
              onClick={() => {
                setSearchQuery("");
                setGlobalFilter("");
              }}
            >
              clear
            </button>
          )}
        </div>
      ) : (
        <></>
      )}
      <table id={id} className="tomo-table" {...getTableProps()}>
        {TableHeaderComponent ? (
          TableHeaderComponent(headerGroups)
        ) : (
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, idx) => {
                  // reload sort order from component state if necessary; this is so sorting plays nice with infinite scroll
                  if (sortState.idx === idx && !column.isSorted)
                    column.toggleSortBy(sortState.order, false);

                  // hacky, modified version of ...column.getHeaderProps(column.getSortByToggleProps()) to support that
                  return (
                    <th
                      className={column.unsortable ? "" : "cursor-pointer"}
                      onClick={e => {
                        if (customSort) {
                          const nextOrder =
                            sortState.idx === idx
                              ? sortState.order
                                ? 0
                                : true
                              : false;
                        }
                        if (column.unsortable) return;
                        const nextOrder =
                          sortState.idx === idx
                            ? sortState.order
                              ? 0
                              : true
                            : false;
                        if (nextOrder === 0) column.clearSortBy();
                        else column.toggleSortBy(nextOrder, false);
                        setSortState({
                          idx: nextOrder === 0 ? -1 : idx,
                          order: nextOrder
                        });
                      }}
                      key={idx}
                    >
                      <div className="flex items-center">
                        {" "}
                        {/* justify-between to put arrows on the right */}
                        <span>{column.render("Header")}</span>
                        {column.unsortable || (
                          <i
                            className={`ml-1 fa fa-sort${
                              column.isSorted
                                ? column.isSortedDesc
                                  ? "-down"
                                  : "-up"
                                : ""
                            }`}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
        )}
        <tbody {...getTableBodyProps()}>
          {toRender.length ? (
            toRender.map(
              row =>
                prepareRow(row) || (
                  // Use a React.Fragment here so the table markup is still valid
                  <Fragment key={row.id}>
                    <tr
                      {...row.getRowProps()}
                      style={getRowStyle(row)}
                      className={classnames("hover:bg-gray-100", {
                        "border-none": row.isExpanded,
                        "cursor-pointer": isClickable
                      })}
                      onClick={e => isClickable && onRowClick(e, row)}
                    >
                      {row.cells.map(cell => (
                        <td
                          {...cell.getCellProps({
                            className: cell.column.className,
                            style: cell.column.style
                          })}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                    {SubComponent && row.isExpanded && (
                      <tr
                        className={classnames("sub-row", {
                          "border-none": !row.isExpanded
                        })}
                      >
                        <td colSpan={columns.length}>
                          <SubComponent row={row} {...subComponentProps} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
            )
          ) : (
            <tr>
              <td style={{ textAlign: "left" }}>
                {emptyStateText || "No Results"}
              </td>
              <td />
            </tr>
          )}
        </tbody>
        <tfoot>{TableFooterComponent ? <TableFooterComponent /> : null}</tfoot>
      </table>
    </>
  );
};

Table.propTypes = propTypes;
Table.defaultProps = defaultProps;

export default Table;